/**
 * AhaRibbon — reusable page-local sign-in nudge.
 *
 * Renders an amber ribbon at the top of the current page prompting the
 * signed-out user to save their progress. Each instance takes a unique
 * `storageKey` so dismissals don't leak between contexts. Dismissed 24h.
 *
 * ── A/B split-test ────────────────────────────────────────────────
 * Every ribbon has two copy variants:
 *   • `benefit` — gain-framed ("Save it to any device …")
 *   • `urgency` — loss-framed ("Only saved on this device …")
 * Assignment is deterministic per (anonymousId + storageKey) so a user
 * always sees the same copy on the same ribbon (no flicker between
 * visits). Exposures + clicks are logged to /api/journey/aha_event so
 * we can compare sign-in rates.
 *
 * Callers can either pass explicit `benefit={{title, subtitle}}` /
 * `urgency={{...}}` payloads or accept the defaults.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Cloud, Sparkles, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import { api, API } from "@/lib/api";

const DEFAULTS = {
  benefit: {
    title:    "Nice — you've built real progress.",
    subtitle: "Save it to any device and get deadline reminders.",
    icon:     Sparkles,
    tone:     "amber",   // amber = celebratory / positive
  },
  urgency: {
    title:    "Only saved on this device.",
    subtitle: "Sync now so a browser wipe or new phone doesn't erase your work.",
    icon:     AlertTriangle,
    tone:     "rose",    // rose = mild-loss framing, softer than red
  },
};

// Deterministic 50/50 assignment: hash the token + ribbon into an int
// and pick benefit vs urgency. Same input → same variant every visit.
function assignVariant(anonymousId, ribbonKey) {
  const str = `${anonymousId || "anon"}::${ribbonKey || ""}`;
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % 2 === 0 ? "benefit" : "urgency";
}

async function reportEvent(anonymous_id, ribbon_key, variant, event) {
  if (!anonymous_id || !ribbon_key || !variant) return;
  const payload = { anonymous_id, ribbon_key, variant, event };
  // signin_click is followed by an immediate navigation to the OAuth
  // provider, which can kill an in-flight fetch. sendBeacon is designed
  // exactly for this — the browser guarantees delivery even mid-nav.
  if (event === "signin_click" && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const url = `${API}/journey/aha_event`;
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    } catch { /* fall through to fetch */ }
  }
  try {
    await api.post("/journey/aha_event", payload);
  } catch { /* best-effort */ }
}

const TONE_STYLES = {
  amber: {
    wrap:   "bg-gradient-to-r from-amber-50 via-amber-50 to-orange-50 border-b border-amber-200",
    iconBg: "bg-amber-200 text-amber-800",
    title:  "text-amber-900",
    body:   "text-amber-800",
    dismiss:"text-amber-700/70 hover:text-amber-900",
  },
  rose: {
    wrap:   "bg-gradient-to-r from-rose-50 via-rose-50 to-orange-50 border-b border-rose-200",
    iconBg: "bg-rose-200 text-rose-800",
    title:  "text-rose-900",
    body:   "text-rose-800",
    dismiss:"text-rose-700/70 hover:text-rose-900",
  },
};

export default function AhaRibbon({
  storageKey,
  benefit,
  urgency,
  ctaLabel = "Sign in",
  testId = "aha-ribbon",
  visible = true,
}) {
  const { user, login } = useAuth();
  const { anonymousId } = useJourney();
  const [show, setShow] = useState(false);

  // Deterministic variant assignment. If either title override is
  // missing we fall back to that side's default so callers can override
  // just one variant if desired.
  const variant = useMemo(
    () => assignVariant(anonymousId, storageKey),
    [anonymousId, storageKey],
  );
  const content = useMemo(() => {
    const override = variant === "benefit" ? benefit : urgency;
    return { ...DEFAULTS[variant], ...(override || {}) };
  }, [variant, benefit, urgency]);

  useEffect(() => {
    if (user || !visible) { setShow(false); return; }
    try {
      const raw = localStorage.getItem(storageKey);
      const dismissed = raw ? Date.now() < parseInt(raw, 10) : false;
      setShow(!dismissed);
    } catch { setShow(true); }
  }, [user, visible, storageKey]);

  // Fire a `shown` event exactly once per session per ribbon+variant.
  useEffect(() => {
    if (!show || !anonymousId) return;
    const shownKey = `wua:aha:shown:${storageKey}:${variant}`;
    try {
      if (sessionStorage.getItem(shownKey)) return;
      sessionStorage.setItem(shownKey, "1");
    } catch { /* private mode — just fire */ }
    reportEvent(anonymousId, storageKey, variant, "shown");
  }, [show, anonymousId, storageKey, variant]);

  if (!show || user || !visible) return null;

  const tone = TONE_STYLES[content.tone] || TONE_STYLES.amber;
  const Icon = content.icon || Sparkles;

  const handleSignIn = () => {
    reportEvent(anonymousId, storageKey, variant, "signin_click");
    login();
  };
  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, String(Date.now() + 24 * 60 * 60 * 1000)); } catch { /* ignore */ }
    reportEvent(anonymousId, storageKey, variant, "dismiss");
    setShow(false);
  };

  return (
    <div data-testid={testId} data-variant={variant} className={tone.wrap}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center gap-3">
        <span className={`hidden sm:inline-flex h-6 w-6 items-center justify-center rounded-full shrink-0 ${tone.iconBg}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className={`text-[12px] flex-1 min-w-0 ${tone.title}`}>
          <span className="font-semibold">{content.title}</span>{" "}
          <span className={tone.body}>{content.subtitle}</span>
        </p>
        <button
          type="button"
          onClick={handleSignIn}
          data-testid={`${testId}-signin`}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#002147] hover:bg-[#002147]/90 text-white text-[11px] font-medium shrink-0"
        >
          <Cloud className="h-3 w-3" /> {ctaLabel}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid={`${testId}-dismiss`}
          className={`p-1 shrink-0 ${tone.dismiss}`}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
